// biome-ignore lint/style/noRestrictedImports: <AI generated code>
import { useFlags as originalUseFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { useDevModal } from '~/components/DevModal/hooks/useDevModal';

type FlagValue = boolean | number | string | Record<string, unknown> | unknown[];

/**
 * Helper function to get dev flag overrides from localStorage
 */
function getDevFlagOverrides(): Record<string, FlagValue> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const overridesString = localStorage.getItem('dev-flag-overrides');
    if (!overridesString) {
      return {};
    }

    const overrides = JSON.parse(overridesString);
    return typeof overrides === 'object' && overrides !== null ? overrides : {};
  } catch (error) {
    console.error('Failed to parse dev flag overrides:', error);
    return {};
  }
}

/**
 * Intercepted version of useFlags that applies dev overrides before returning
 */
export function useFlags<T extends Record<string, FlagValue>>(): T {
  const originalFlags = originalUseFlags<T>();
  const { registerDevFlagListener } = useDevModal();
  const [_overrideKey, setOverrideKey] = useState(0);

  // Register with DevModalContext for dev flag override notifications
  useEffect(() => {
    const unregister = registerDevFlagListener(() => {
      setOverrideKey((prev) => prev + 1);
    });

    return unregister;
  }, [registerDevFlagListener]);

  // Additional fallback: listen for storage changes directly
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev-flag-overrides') {
        setOverrideKey((prev) => prev + 1);
      }
    };

    const handleCustomEvent = () => {
      setOverrideKey((prev) => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('dev-flag-override-changed', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dev-flag-override-changed', handleCustomEvent);
    };
  }, []);

  return useMemo(() => {
    const overrides = getDevFlagOverrides();

    // If no overrides, return original flags
    if (Object.keys(overrides).length === 0) {
      return originalFlags;
    }

    // Apply overrides to the original flags
    const effectiveFlags = { ...originalFlags };

    Object.keys(overrides).forEach((flagName) => {
      if (flagName in effectiveFlags) {
        (effectiveFlags as Record<string, FlagValue>)[flagName] = overrides[flagName];
      }
    });

    return effectiveFlags;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalFlags, _overrideKey]);
}
